// A phone, with a real screen of our app inside it.
//
// The screens are SCREENSHOTS of the real worker app and the real Hub, rendered
// from the apps' own code on made up data (scripts/app-screens: capture.mjs,
// the invented cast in fixtures/cast.json). Not drawings: what the site shows
// is what the app shows. Regenerate them when the app UI moves.
//
// The capture carries the app's own top and bottom edge into an empty band, and
// this frame draws the status bar and home indicator over those bands, so there
// is exactly ONE status bar and it reads crisply at any zoom.
//
// Marked aria-hidden: it is a picture of software, and a screen reader walking a
// wallet balance is noise. The section’s words carry the meaning.

// ⚠️ `caption` is NOT decoration. These screens print real looking money, and
// spec §8 requires that a mockup showing money is VISIBLY illustrative: the
// warning living in a source comment protects nobody, and an unlabelled £160
// beside "paid the day you work" is a figure we would have to substantiate.
// Default it on, and let a screen with no money in it pass caption={null}.

// The screen is 280 by 606 CSS px: a 390 by 844 phone at 280 wide.
export const SCREEN_W = 280
export const SCREEN_H = 606

export default function PhoneFrame({ className = '', caption = 'Illustration', children }) {
  return (
    <div className={`shrink-0 ${className}`}>
      <div
        aria-hidden="true"
        className="relative w-[292px] rounded-[47px] bg-[#1A1D21] p-[6px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.55),0_0_0_1px_rgba(255,255,255,0.08)_inset]"
        style={{ backgroundImage: 'linear-gradient(145deg,#3A3F46 0%,#16181B 30%,#0E0F11 70%,#2B2F35 100%)' }}
      >
        {/* Side buttons: action and volume left, power right. */}
        <span className="absolute -left-[2px] top-[96px] h-[22px] w-[3px] rounded-l-[2px] bg-[#2A2E33]" />
        <span className="absolute -left-[2px] top-[136px] h-[44px] w-[3px] rounded-l-[2px] bg-[#2A2E33]" />
        <span className="absolute -left-[2px] top-[190px] h-[44px] w-[3px] rounded-l-[2px] bg-[#2A2E33]" />
        <span className="absolute -right-[2px] top-[150px] h-[68px] w-[3px] rounded-r-[2px] bg-[#2A2E33]" />

        <div className="relative overflow-hidden rounded-[41px] bg-black" style={{ width: SCREEN_W, height: SCREEN_H }}>
          {children}

          {/* Status bar, over the capture's empty top band. */}
          <div className="absolute inset-x-0 top-0 flex h-[36px] items-center justify-between px-[26px] pt-[2px] text-[13px] font-semibold text-[#0F1720]">
            <span className="tabular-nums tracking-[-0.01em]">9:41</span>
            <svg width="52" height="12" viewBox="0 0 54 12" fill="currentColor">
              <rect x="0" y="7" width="3" height="5" rx="1" /><rect x="5" y="5" width="3" height="7" rx="1" />
              <rect x="10" y="3" width="3" height="9" rx="1" /><rect x="15" y="0" width="3" height="12" rx="1" />
              <path d="M28 3.5a8 8 0 0 1 9 0l-1.2 1.4a6 6 0 0 0-6.6 0zM30.2 6.3a4.5 4.5 0 0 1 4.6 0L32.5 9z" />
              <rect x="40.5" y="1" width="12" height="10" rx="2.5" fill="none" stroke="currentColor" strokeOpacity=".4" />
              <rect x="42" y="2.5" width="8" height="7" rx="1.2" />
            </svg>
          </div>
          {/* Dynamic island. */}
          <div className="absolute left-1/2 top-[9px] h-[24px] w-[82px] -translate-x-1/2 rounded-full bg-black" />
          {/* Home indicator, over the capture's empty bottom band. */}
          <div className="absolute bottom-[7px] left-1/2 h-[4px] w-[96px] -translate-x-1/2 rounded-full bg-[#0F1720]/80" />
          {/* Glass: the faintest sheen across the screen. */}
          <div
            className="pointer-events-none absolute inset-0 rounded-[41px]"
            style={{ background: 'linear-gradient(118deg,rgba(255,255,255,0.16) 0%,rgba(255,255,255,0.04) 28%,rgba(255,255,255,0) 42%)' }}
          />
        </div>
      </div>
      {caption && (
        <p className="mt-3 w-[292px] text-center text-[12px] text-ink-muted">{caption}</p>
      )}
    </div>
  )
}

// One captured app screen (public/app-screens/<id>-{560,840}.{avif,webp}, PNG
// fallback). Explicit size so nothing shifts as it loads; lazy unless `eager`
// (a phone above the fold).
export function AppScreen({ id, eager = false }) {
  const src = (w, ext) => `/app-screens/${id}-${w}.${ext}`
  const set = (ext) => `${src(560, ext)} 2x, ${src(840, ext)} 3x`
  return (
    <picture>
      <source type="image/avif" srcSet={set('avif')} />
      <source type="image/webp" srcSet={set('webp')} />
      <img
        src={src(560, 'png')}
        alt=""
        width={SCREEN_W}
        height={SCREEN_H}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        className="block h-full w-full"
      />
    </picture>
  )
}
